import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { Timeline as TimelineIcon } from '@mui/icons-material';
import {
  ExecutionsPerWeekChart,
  ProviderDistributionChart,
  TopModelsChart,
  CostComparisonChart,
  CostBreakdownChart,
} from '../../charts';
import { SHARED_TYPOGRAPHY } from '../../../theme';

/**
 * AnalyticsChartsSection Component
 *
 * Displays the four new analytics charts in a 2x2 grid:
 * - Executions per week (line chart)
 * - Provider distribution (pie chart)
 * - Top 5 models (bar chart)
 * - Cost comparison (bar chart)
 */
const AnalyticsChartsSection = ({
  executionsPerWeek,
  providerDistribution,
  topModels,
  costComparison,
  costBreakdown,
  loading = false,
}) => {
  // Don't render if no data and not loading
  // For all-time view, only Executions Per Week is shown (others are null)
  const hasAnyData =
    executionsPerWeek ||
    providerDistribution ||
    topModels ||
    costComparison ||
    costBreakdown;
  if (!loading && !hasAnyData) {
    return null;
  }

  return (
    <Box sx={{ mt: 4 }}>
      {/* Section Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <TimelineIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" sx={SHARED_TYPOGRAPHY.h4Bold}>
          Analytics Overview
        </Typography>
      </Box>

      {/* Cost Charts Row */}
      {costBreakdown &&
        costBreakdown.models &&
        costBreakdown.models.length > 0 && (
          <Grid container spacing={3} sx={{ alignItems: 'stretch', mb: 3 }}>
            {/* Cost Breakdown Chart */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <CostBreakdownChart
                    data={costBreakdown}
                    period={costBreakdown?.period}
                    loading={loading}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Cost Comparison Chart */}
            {costComparison &&
              costComparison.cost_comparison &&
              costComparison.cost_comparison.length > 0 && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <CostComparisonChart
                        data={costComparison}
                        period={costComparison?.period}
                        loading={loading}
                      />
                    </Box>
                  </Box>
                </Grid>
              )}
          </Grid>
        )}

      {/* Analytics Charts Row */}
      <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
        {/* Executions Per Week Chart - Always shown (30d for all-time) */}
        {executionsPerWeek && executionsPerWeek.weekly_executions && (
          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <ExecutionsPerWeekChart
                  data={executionsPerWeek}
                  period={executionsPerWeek?.period}
                  loading={loading}
                />
              </Box>
            </Box>
          </Grid>
        )}

        {/* Provider Distribution Chart */}
        {providerDistribution && providerDistribution.provider_distribution && (
          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <ProviderDistributionChart
                  data={providerDistribution}
                  period={providerDistribution?.period}
                  loading={loading}
                />
              </Box>
            </Box>
          </Grid>
        )}

        {/* Top Models Chart */}
        {topModels &&
          topModels.top_models &&
          topModels.top_models.length > 0 && (
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <TopModelsChart
                    data={topModels}
                    period={topModels?.period}
                    loading={loading}
                  />
                </Box>
              </Box>
            </Grid>
          )}
      </Grid>
    </Box>
  );
};

export default AnalyticsChartsSection;
